// The non-null assertions are acceptable because there definitely will always
// be data in the successful responses where the non-null assertions are made.
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
  BigCommerceResponse,
  JimboResponse,
  Order,
  OrderProduct,
  OrderRequest,
  OrderRefundResponse,
  OrderMetafield,
  OrderRefundRequest,
  OrderStatus,
  BigCommerceEndpointTagTypes,
  ShippingAddress,
  InvoiceHandler,
  InvoiceHandlerResponse,
} from '@models';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const orderEndpoints = (
  builder: EndpointBuilder<
    // eslint-disable-next-line @typescript-eslint/ban-types
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    BigCommerceEndpointTagTypes,
    'bigCommerceApi'
  >,
) => {
  return {
    getOrdersByCustomerId: builder.query<Order[], { customerId: number }>({
      query: (query) => `orders?customerId=${query.customerId}`,
      providesTags: ['Order'],
      transformResponse: (response: BigCommerceResponse<Order[]>) => response.data!,
    }),
    getOrderStatus: builder.query<number, { orderId: number }>({
      query: (query) => `orders/getstatus/${query.orderId}`,
      providesTags: ['Order'],
      transformResponse: (response: BigCommerceResponse<number>) => response.data!,
    }),
    updateOrder: builder.mutation<Order, OrderRequest>({
      query: (order) => ({
        url: 'orders',
        method: 'PUT',
        body: order,
      }),
      invalidatesTags: ['Order'],
      transformResponse: (response: JimboResponse<Order>) => response.data!,
    }),
    issueOrderRefund: builder.mutation<JimboResponse<OrderRefundResponse>, { orderId: number; data: OrderRefundRequest }>({
      query: ({ orderId, data }) => ({
        url: `orders/${orderId}/refund`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Order'],
      transformResponse: (response: JimboResponse) => response.data!,
    }),
    getOrderProducts: builder.query<OrderProduct[], number>({
      query: (orderId) => ({
        url: `orders/${orderId}/products`,
      }),
      transformResponse: (response: JimboResponse<OrderProduct[]>) => response.data!,
    }),
    getMetafieldsByOrderId: builder.query<OrderMetafield[], { orderId: number }>({
      query: (query) => `orders/${query.orderId}/metafields`,
      transformResponse: (response: BigCommerceResponse<JimboResponse<OrderMetafield[]>>) => response.data.data!,
    }),
    createMetafieldForOrder: builder.mutation<OrderMetafield, { orderId: number; metafield: OrderMetafield }>({
      query: (query) => ({
        url: `orders/${query.orderId}/metafields`,
        method: 'POST',
        body: query.metafield,
      }),
      transformResponse: (response: JimboResponse<OrderMetafield>) => response.data!,
    }),
    getShippingAddress: builder.query<ShippingAddress[], number>({
      query: (orderId) => ({
        url: `orders/${orderId}/shippingAddress`,
      }),
      transformResponse: (response: JimboResponse<ShippingAddress[]>) => response.data!,
    }),
    getOrderStatuses: builder.query<OrderStatus[], void>({
      query: () => `orders/order-statuses`,
      transformResponse: (response: JimboResponse<OrderStatus[]>) => response.data!,
    }),
    getOrderStatusById: builder.query<OrderStatus, { statusId: number }>({
      query: (query) => `orders/order-status/${query.statusId}`,
      transformResponse: (response: BigCommerceResponse<OrderStatus>) => response.data!,
    }),
    syncVoucher: builder.mutation<JimboResponse, { customerId: number }>({
      query: (query) => ({
        url: `orders/sync-voucher/${query.customerId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Customer'],
    }),
    handleInvoice: builder.mutation<InvoiceHandlerResponse, InvoiceHandler>({
      query: (payload) => ({
        url: `orders/handle-invoice`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Order'],
      transformResponse: (response: JimboResponse<InvoiceHandlerResponse>) => response.data!,
    }),
  };
};
